import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";

// Status label mapping (i18n)
const STATUS_KEY = {
  active: "billing_status_active",
  trialing: "billing_status_trialing",
  canceled: "billing_status_canceled",
  past_due: "billing_status_past_due",
  none: "billing_status_none",
  incomplete: "billing_status_incomplete",
};

export default function Billing() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    document.title = lang === "es" ? "Mi plan · GalascioDAS™" : "My plan · GalascioDAS™";
  }, [lang]);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/billing/me");
      setMe(r.data);
    } catch (e) {
      toast.error("Error loading billing info");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    reload();
  }, [user]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post("/billing/portal", { origin_url: window.location.origin });
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "no_stripe_customer") {
        toast.info(detail.message);
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error(detail?.message || "Stripe portal error");
      }
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading || !me) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-[1400px] mx-auto px-6 py-24 font-mono-tech text-xs tracking-widest text-[#888]">
          · Loading…
        </div>
      </div>
    );
  }

  const planLabel = t(`plan_${me.plan}_name`) || me.plan.toUpperCase();
  const statusLabel = me.plan === "legacy"
    ? t("billing_status_legacy")
    : t(STATUS_KEY[me.subscription_status] || "billing_status_none");
  const isActive = me.is_premium;
  const periodEnd = me.current_period_end ? me.current_period_end.slice(0, 10) : null;
  // A user is manually managed when:
  //   - backend explicitly says so (manual_managed=true), OR
  //   - they are legacy/consulting and have no Stripe customer
  // This is defensive against payload variations.
  const isManualManaged =
    me.manual_managed === true ||
    ((me.plan === "legacy" || me.plan === "consulting") && !me.stripe_customer_id);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <main data-testid="billing-page" className="max-w-[1100px] mx-auto px-6 md:px-10 py-12">
        <div className="font-mono-tech text-[10px] tracking-[0.3em] text-[#D90429] mb-3">
          · {t("billing_title")}
        </div>
        <h1
          data-testid="billing-title"
          className="font-serif-display text-4xl md:text-5xl font-bold mb-10"
        >
          {t("billing_title")}
        </h1>

        {/* Current plan card */}
        <section className="border border-[#0A0A0A] bg-white p-8 mb-8">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="font-mono-tech text-[10px] tracking-[0.3em] text-[#888] uppercase mb-2">
                · {lang === "es" ? "Plan actual" : "Current plan"}
              </div>
              <div
                data-testid="billing-plan-name"
                className="font-serif-display text-4xl md:text-5xl font-bold mb-2 tracking-tight"
              >
                {planLabel}
              </div>
              <div
                data-testid="billing-plan-status"
                className={`inline-block font-mono-tech text-xs tracking-[0.2em] uppercase px-3 py-1 ${
                  isActive
                    ? "bg-[#0A0A0A] text-white"
                    : "border border-[#D90429] text-[#D90429]"
                }`}
              >
                · {statusLabel}
              </div>
              {periodEnd && (
                <div className="mt-3 font-mono-tech text-xs text-[#525252]">
                  {lang === "es" ? "Renovación" : "Renews"}: {periodEnd}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3">
              {me.stripe_customer_id ? (
                <button
                  data-testid="billing-portal-btn"
                  onClick={openPortal}
                  disabled={portalLoading}
                  className="px-6 py-3 border-2 border-[#0A0A0A] bg-[#0A0A0A] text-white hover:bg-[#D90429] hover:border-[#D90429] font-mono-tech text-xs uppercase tracking-[0.2em] disabled:opacity-50"
                >
                  {portalLoading ? t("plan_cta_loading") : t("billing_manage")}
                </button>
              ) : isManualManaged ? (
                <div
                  data-testid="billing-manual-managed"
                  className="max-w-xs text-xs text-[#525252] border-l-2 border-[#D90429] pl-3 py-2 leading-relaxed"
                >
                  {t("billing_manual_managed")}
                </div>
              ) : (
                <Link
                  to="/pricing"
                  data-testid="billing-view-plans"
                  className="px-6 py-3 border-2 border-[#D90429] bg-[#D90429] text-white hover:bg-[#A60023] hover:border-[#A60023] font-mono-tech text-xs uppercase tracking-[0.2em]"
                >
                  {t("billing_view_plans")} →
                </Link>
              )}
            </div>
          </div>
        </section>

        {/* Premium status */}
        <section className="border border-[#E5E5E5] bg-white p-6">
          <div className="font-mono-tech text-[10px] tracking-[0.3em] text-[#888] mb-3">
            · {lang === "es" ? "Funciones premium" : "Premium features"}
          </div>
          <p
            data-testid="billing-premium-msg"
            className="text-base font-serif-display italic leading-snug"
          >
            {isActive ? (
              <span className="text-[#0A0A0A]">
                ✓ {lang === "es"
                  ? "Tienes acceso a tendencias, comparativos y alertas."
                  : "You have access to trends, compare and alerts."}
              </span>
            ) : (
              <span className="text-[#404040]">
                {t("billing_no_premium")}{" "}
                <Link to="/pricing" className="text-[#D90429] hover:underline">
                  {t("billing_view_plans")} →
                </Link>
              </span>
            )}
          </p>
        </section>
      </main>
    </div>
  );
}
